import {BUBBLE, Component, DONE, isCopyModifier, TOUCH} from "@/event/calendar/dom/Component.ts";
import {DrawingContext} from "@/event/calendar/dom/DrawingContext.ts";
import {CANCELED_OPACITY, Coordinates, decodeNumericIconEntity, distance, DONE_OPACITY, DONE_SATURATION, drawHoliday, intersection, MOVE_OPACITY, parseHexColor, Range, Rect, reduceSaturation, rgbToHex, SnapCoordinates} from "@/event/calendar/Shapes.ts";
import {DayColumn, DayColumns, HasDayColumns, selectedDateToAligned, weekGridDays} from "@/event/calendar/days/DaysComponent.ts";
import {drawVerticalLines} from "@/event/calendar/days/DaysTopHeader.ts";
import {CalendarView, DISPLAY_CUSTOMER, DISPLAY_DESCRIPTION, DISPLAY_PLACE, DISPLAY_SUBJECT, saveView} from "@/event/calendar/CalendarModel.ts";
import {ICON_FONT, MAX_HOUR_HEIGHT, MIN_HOUR_HEIGHT} from "@/event/calendar/Constants.ts";
import {MIN_SNAP_DISTANCE, ScrollComponent} from "@/event/calendar/dom/ScrollComponent.ts";
import {calcLayerSpan, calculateEventLayers, CalEvent, DateRange, DUMMY_ID, EventRect, overlaps, normalizeRange, timeFormat, timeRange, uidToPreviewKey} from "@/store/CalendarStore.ts";
import {drawBlockText, drawTextBlock, measureRuns, TextRun} from "@/canvas/CanvasUtils.ts";
import {Icon} from "anneca-symbols";
import {COLOR_NAMES, DEFAULT_EVENT_COLOR} from "@t/fd/editor/EventColor.ts";
import {CanvasRootComponent} from "@/event/calendar/dom/CanvasRootComponent.ts";
import {sameDay, shiftDate} from "@t/util/DateUtil.ts";
import {getDataTransfer, hasIdentity, ParsedType, parseObjectRef, setDataTransfer, uidToRef} from "@/utils/DragDropUtils.ts";

export type MouseStartAction = "bg" | "start" | "end" | "event" | "button" | "label" | "none";
export type MouseActiveAction = "create" | "resize-start" | "resize-end";
export type MouseAction = "idle" | MouseStartAction | MouseActiveAction;


/** Number of pixels an event can peek out of off-screen area */
const EVENT_PEEK = 4;
const TIP_HEIGHT = 26;
const TIP_OFFSET = 8;
const TIP_SPACING = 4;
const TIP_BASELINE = 18;
const TIP_PADDING = 8;
export const LONG_PRESS_TIME:number = 500;

export class DaysTimedBody extends Component implements HasDayColumns {
	public dayColumns: DayColumns;
	private dropDate:Date|null;
	private view: CalendarView;
	private dayHeight:number;
	private eventShapes:EventRect[];

	/* Mouse actions */
	private startX:number;
	private startY:number;
	private startEventRange: DateRange;	// Original event time span
	private action: MouseAction;
	private actionObject:EventRect;

	/** Create new placeholder */
	private createDummy: CalEvent;

	constructor(view: CalendarView) {
		super();
		this.view = view;
		this.eventShapes = [];
		this.action = "idle";
		this.actionObject = null;
		this.dropDate = null;
		this.createDummy = null;
	}


	protected drawBackground(dc: DrawingContext, area: Rect) {
		dc.ctx.fillStyle = dc.style.getPropertyValue("--cal-bg");
		dc.ctx.fillRect(area.x, area.y, area.width, area.height);


		// Fill weekend backgrounds
		const weekendBodyColor = dc.style.getPropertyValue("--cal-weekend-bg");
		const todayBodyColor = dc.style.getPropertyValue("--cal-today-body");
		const holidayColor = dc.style.getPropertyValue("--cal-today-bg");
		const fontFamily = dc.style.getPropertyValue("--font");

		dc.ctx.fillStyle = weekendBodyColor;
		for (let col of this.dayColumns.days) {
			const day = col.date.getDay();
			if (col.today) {
				dc.ctx.fillStyle = todayBodyColor;
				let dayRect = { x: col.x, y: -10000, width: col.width, height: 20000 };
				dayRect = intersection(dayRect, area);
				dc.ctx.fillRect(dayRect.x, dayRect.y, dayRect.width, dayRect.height);
			} else if (day === 0 || day === 6) { /* sunday, saturday */
				dc.ctx.fillStyle = weekendBodyColor;
				let dayRect = { x: col.x, y: -10000, width: col.width, height: 20000 };
				dayRect = intersection(dayRect, area);
				dc.ctx.fillRect(dayRect.x, dayRect.y, dayRect.width, dayRect.height);
			}
			const h = this.view.store.getHoliday(col.date);
			if (h) drawHoliday(dc.ctx, h, { x: col.x, y: area.y, width: col.width, height: area.height}, fontFamily, holidayColor);
		}

		const starth = typeof this.view.startHour === "number" ? this.view.startHour : 8;
		const endh = typeof this.view.endHour === "number" ? this.view.endHour : 18;

		const hour = calcTimeGrid(this.view, this.height);
		let first = true;
		for (let h = 0; h < 24; h++) {
			const hourStartY = this.y + hour.height * h;
			for (let hline of hour.separators) {
				if (first) {
					first = false;
					continue;	// ignore first line (is directly at the top)
				}
				let color;
				switch (hline.type) {
					case "15m": color = dc.style.getPropertyValue("--cal-15m-separ"); break;
					case "30m": color = dc.style.getPropertyValue("--cal-30m-separ"); break;
					default: case "1h": color = dc.style.getPropertyValue("--cal-hour-separ"); break;
				}
				let workHours = !(h < starth || h === endh && hline.type !== "1h" || h > endh);
				if (workHours) dc.ctx.setLineDash([]);
				else dc.ctx.setLineDash([4, 4]);
				dc.ctx.strokeStyle = color;
				dc.ctx.beginPath();
				dc.ctx.moveTo(area.x, hourStartY + hline.y);
				dc.ctx.lineTo(area.x + area.width, hourStartY + hline.y);
				dc.ctx.stroke();
			}
		}
		dc.ctx.setLineDash([]);
		this.dayHeight = 24 * hour.height;

		dc.ctx.strokeStyle = dc.style.getPropertyValue("--cal-day-separ");
		drawVerticalLines(dc.ctx, this.dayColumns, area);

		let dayCursor:DayColumn = null;
		for (let col of this.dayColumns.days) {
			if (col.today) {
				dayCursor = col;
				break;
			}
		}

		if (dayCursor) {
			const cursorColor = dc.style.getPropertyValue("--cal-cursor");
			dc.ctx.strokeStyle = cursorColor+"80";
			dc.ctx.beginPath();
			dc.ctx.moveTo(area.x, hour.cursor);
			dc.ctx.lineTo(area.x + area.width, hour.cursor);
			dc.ctx.stroke();

			dc.ctx.beginPath();
			dc.ctx.arc(dayCursor.x, hour.cursor, 3, 0, 2 * Math.PI);
			dc.ctx.fillStyle = cursorColor;
			dc.ctx.fill();
		}
	}

	initialScroll(): { x?: number; y?: number } | null {
		const hour = calcTimeGrid(this.view, this.height);
		const dayHeight = 24 * hour.height;
		const starth = this.view.startHour ?? 8;
		const endh = this.view.endHour ?? 18;
		const avgh = Math.floor((starth + endh + 1) / 2);
		let topScroll = avgh * hour.height - (this.height / 2);
		const maxScroll = Math.max(dayHeight - this.height, 0);
		if (topScroll >  maxScroll) topScroll = maxScroll;
		else if (topScroll < 0) topScroll = 0;
		//console.log("Initial scroll " + topScroll + " max = " + maxScroll);
		return { y : topScroll };
	}

	/** Draw events */
	protected drawContent(dc: DrawingContext, area: Rect) {
		const tg = calcTimeGrid(this.view, this.height);

		const eventShapes:EventRect[] = [];

		const minY = area.y + EVENT_PEEK;
		const maxY = area.y + area.height - EVENT_PEEK;

		let dummyRect:EventRect = null;

		for (let d of this.dayColumns.days) {
			const r = normalizeRange(d.date, d.date);
			const allEvents:CalEvent[] = this.view.store.getTimedEvents(r.start, r.end);
			if (this.createDummy && overlaps(r, { start: this.createDummy.start, end: this.createDummy.end })) allEvents.push(this.createDummy);
			//debugger;
			const layers: CalEvent[][] = calculateEventLayers(allEvents);
			if (layers.length > 0) {
				let indent = 20;
				let layerWidth = d.width / layers.length;
				for (let l = 0; l < layers.length; l++) {
					const events = layers[l];
					for (let ev of events) {
						const ypos = calcVertPosition(d.date, ev.start, ev.end, tg);
						const span = calcLayerSpan(ev, layers, l);
						let overlap = 0;
						if ((l + span) < layers.length) overlap = 4;
						const eventX = d.x+1 + (l * layerWidth);
						let eventY = ypos.min;
						const eventWidth = layerWidth*span -1 + overlap;
						let eventHeight = ypos.max - ypos.min;
						if (eventY > maxY) {
							// Out of screen at bottom
							eventY = maxY;
						} else if ((eventY + eventHeight) < minY) {
							eventHeight = minY - eventY;
						}
						let erect = drawEventBlock(dc, ev, eventX, eventY, eventWidth, eventHeight, false, this.view);
						if (ev.uid === DUMMY_ID) dummyRect = erect;
						else eventShapes.push(erect);
					}
				}
			}
		}
		this.eventShapes = eventShapes;

		/** Optionally draw drop cursor */
		if (this.dropDate) {
			for (let d of this.dayColumns.days) {
				if (sameDay(d.date, this.dropDate)) {
					const isCopy = ("copy" === this.cursor);
					dc.ctx.fillStyle = dc.style.getPropertyValue("--selection-bg");
					dc.ctx.strokeStyle = dc.ctx.fillStyle;
					const ypos = Math.floor(yPosition(this.dropDate, tg.height));
					dc.ctx.beginPath();
					dc.ctx.moveTo(d.x, ypos + 1);
					dc.ctx.lineTo(d.x + d.width, ypos + 1);
					dc.ctx.lineWidth = 2;
					dc.ctx.stroke();
					let textBaseline = ypos - 4;
					let textStart = d.x + 2;
					if (isCopy) {
						const cx = d.x + 1.5 + PLUS_RADIUS;
						const cy = ypos - 1.5 - PLUS_RADIUS;
						drawPlusBullet(dc, cx, cy);
						textBaseline = cy + 5;
						textStart = cx + PLUS_RADIUS + 3;
					}
					dc.ctx.lineWidth = 1;

					const time = timeFormat(this.dropDate);
					const ff = dc.style.getPropertyValue("--font");
					dc.ctx.font = "12px "+ff;
					dc.ctx.fillText(time, textStart, textBaseline);
					break;
				}
			}
		}

		if (dummyRect && !TOUCH) this.drawDummyTip(dc, dummyRect);
	}

	drawDummyTip(dc: DrawingContext, dummy:EventRect) {
		const ff = dc.style.getPropertyValue("--font");
		try {
			dc.ctx.save();
			dc.ctx.fillStyle = "#00000080";
			dc.ctx.beginPath();

			const alt = decodeNumericIconEntity(Icon.parse("key-alt").entity);
			const eq = decodeNumericIconEntity(Icon.parse("equal-8").entity);

			const dragStart = dc.env.msg("calendar.shift.event.start");

			const runs:TextRun[] = [
				{ text: alt, font: "14px "+ICON_FONT, color: "#FFFFFF" },
				{ text: eq, font: "14px "+ICON_FONT, color: "#FFFFFF" },
				{ text: dragStart, font: "12px "+ff, color: "#FFFFFF" }
			];
			const mruns = measureRuns(dc.ctx, runs);
			let totalWidth = (mruns.length - 1) * TIP_SPACING;
			mruns.forEach((mr) => { totalWidth += mr.x });
			const x = dummy.x;
			const y = dummy.y + dummy.height + TIP_OFFSET;
			dc.ctx.roundRect(x, y, totalWidth + 2 * TIP_PADDING, TIP_HEIGHT, 5);
			dc.ctx.fill();
			let start = x + TIP_PADDING;
			const baseline = y + TIP_BASELINE;
			for (let mr of mruns) {
				dc.ctx.font = mr.font;
				dc.ctx.fillStyle = mr.color;
				dc.ctx.fillText(mr.text, start, baseline);
				start += mr.x + TIP_SPACING;
			}

		} finally {
			dc.ctx.restore();
		}
	}

	getSnapPosition(x: number, y: number, xPositiveDir?:boolean, yPositiveDir?:boolean): SnapCoordinates | null {
		const width = this.width / weekGridDays(this.view);
		const lowerSnapX = Math.floor(x / width) * width;
		const higherSnapX = Math.ceil(x / width) * width;
		const lowerDistance = Math.abs(x - lowerSnapX);
		const higherDistance = Math.abs(x - higherSnapX);
		let snapX;
		if (lowerDistance < MIN_SNAP_DISTANCE && higherDistance >= MIN_SNAP_DISTANCE) snapX = lowerSnapX;
		else if (higherDistance < MIN_SNAP_DISTANCE && lowerDistance >= MIN_SNAP_DISTANCE) snapX = higherSnapX;
		else snapX = xPositiveDir ? higherSnapX : lowerSnapX;
		if (x === snapX) return null;
		return { x: snapX, y: 0, snapX:true, snapY: false };
	}

	protected drawBorder(dc: DrawingContext, area: Rect) {
	}


	verticalScrollRange(): Range {
		return {
			min: 0,
			max: Math.max(this.dayHeight - this.height, 0)
		};
	}

	cursorToTime(x:number, y: number, snap:boolean = false):Date {
		const snapMin = this.view.snapMins ?? 1;
		const tg = calcTimeGrid(this.view, this.height);
		for (let d of this.dayColumns.days) {
			if (x >= d.x && x < (d.x + d.width)) {
				const hourPos = (y / tg.height) % 24;
				let hour = Math.floor(hourPos);
				let min = Math.floor((hourPos - hour) * 60);
				const time = new Date(d.date);
				if (snap) {
					min = Math.round(min / snapMin) * snapMin;
					if (min >= 60) {
						min = min - 60;
						hour++;
					}
				}
				time.setHours(hour, min, 0, 0);
				return time;
			}
		}
		return null;
	}

	/**
	 * Change selected date but also alter scroll in such way that
	 * visually the component stays at the same position.
	 * After calling this method there shouldn't be any reason to call dirty()
	 * because the view should look the same as before.
	 * @param newDate for view.selected
	 */
	updateScrollForNewSelectedDate(newDate: Date) {
		if (this.parent instanceof ScrollComponent) {
			const scroll = this.parent as ScrollComponent;
			const dayWidth = this.width / weekGridDays(this.view);
			const currentAlignDate = selectedDateToAligned(this.view.selected);
			const newAlignDate = selectedDateToAligned(newDate);
			// Get the time difference in milliseconds
			const timeDifference = newAlignDate.getTime() - currentAlignDate.getTime();
			// Convert the time difference to days
			const dayDifference = Math.round(timeDifference / (3600000 * 24));
			scroll.scrollX -= dayDifference * dayWidth;
			this.view.selected = newDate;
			this.view.aligned = newAlignDate;
			saveView(this.view);
		}
	}

	scrollIdle() {
		setNewDateAfterScrolling(this.view, this.dayColumns);
	}

	/**
	 * Return date of the first fully visible day column. This column is the leftmost.
	 * Partially visible cells doesn't count.
	 */
	firstVisibleDate():Date {
		if (this.parent instanceof ScrollComponent) {
			const scroll = this.parent as ScrollComponent;
			const dayWidth = this.width / weekGridDays(this.view);
			const currentAlignDate = selectedDateToAligned(this.view.selected);
			const fullDays = Math.floor((scroll.scrollX + 0.9 * dayWidth) / dayWidth);
			shiftDate(currentAlignDate, fullDays);
			return currentAlignDate;
		}
	}

	beginDurationEdit(e: PointerEvent, x: number, y: number, er: EventRect, fromStart:boolean):boolean {
		console.log("Duration edit from " + (fromStart ? "start" : "end"));
		this.action = fromStart ? "start" : "end";
		return false;
	}

	onDragStart(e: DragEvent, x: number, y: number): boolean {
		super.onDragStart(e, x, y);
		// Set custom drag image
		const root = this.root();
		const dragImage = createEventDragImage(this.actionObject, root); // Function to create your custom drag image
		const offsetX = Math.floor(this.startX - this.actionObject.x);
		const offsetY = Math.floor(this.startY - this.actionObject.y);
		//console.log("offsetX", offsetX, "offsetY", offsetY);
		e.dataTransfer.setDragImage(dragImage, offsetX, offsetY);

		// Allow for both copy and move
		e.dataTransfer.effectAllowed = 'copyMove';
		// Optionally, set data in the dataTransfer object
		setDataTransfer(e.dataTransfer, { ox:offsetX, oy:offsetY, ds:0 /* day shift - used to drop on all-day */ }, uidToRef(this.actionObject.event.uid));

		this.actionObject.event.state = "move";
		this.view.store.manipulationStart(this.actionObject.event.uid);
		this.dirty(false);
		return false;
	}

	onDragEnd(e: DragEvent, x: number, y: number): boolean {
		this.root().canvas.removeAttribute('draggable');
		this.view.store.manipulationEnd();
		this.actionObject.event.state = "normal";
		this.dropDate = null;
		this.dirty(false);
		return true;
	}


	onDrop(e: DragEvent, x: number, y: number): boolean {
		this.dropDate = null;	// remove marker
		let pt:ParsedType = getDataTransfer(e.dataTransfer);
		if (pt) {
			//console.log("onDrop type "+JSON.stringify(pt));
			let offsetY = typeof pt.params?.oy === "string" ? parseInt(pt.params.oy) : 0;
			const eventStartPos = y - offsetY;		// corrected y position
			const dpos = this.cursorToTime(x, eventStartPos, true);
			if (dpos != null) {
				e.preventDefault();	// accepting drop
				let oref = parseObjectRef(e.dataTransfer.getData(pt.full));
				if (oref) {
					if (hasIdentity(oref)) {
						const isCopy = this.cursor === "copy";
						if (isCopy) this.view.store.invokeCopy(false, dpos, oref);
						else this.view.store.update(oref).makeTimed().move(dpos).commit();
					} else {
						const dend = new Date(dpos.getTime() + 3600000);
						this.view.store.invokeCreate(false, dpos, dend, oref);
					}
				}
			}
		}
		this.dirty(false);
		return true;
	}


	onDragOver(e: DragEvent, x: number, y: number): boolean {
		// Find data type we can accept
		let pt:ParsedType = getDataTransfer(e.dataTransfer);
		if (pt) {
			let offsetY = typeof pt.params?.oy === "string" ? parseInt(pt.params.oy) : 0;
			const eventStartPos = y - offsetY;		// corrected y position
			const dpos = this.cursorToTime(x, eventStartPos, true);
			if (dpos != null) {
				e.preventDefault();	// accepting drop
				// Detect modifier key (Ctrl typically means copy)
				if (isCopyModifier(e)) {
					e.dataTransfer.dropEffect = 'copy';
					this.setCursorForDragEffect("copy");
				} else {
					e.dataTransfer.dropEffect = 'move';
					this.setCursorForDragEffect("move");
				}

				const change = this.dropDate == null || this.dropDate.getTime() !== dpos.getTime();
				this.dropDate = dpos;
				if (change) this.dirty(false);
			}
		}
		return true;
	}

	onDragEnter(e: DragEvent, x: number, y: number): boolean {
		return this.onDragOver(e, x, y);
	}

	onDragLeave(e: DragEvent, x: number, y: number): boolean {
		const change = this.dropDate != null;
		this.dropDate = null;
		if (change) this.dirty(false);
		return true;
	}


	determineStartAction(e: PointerEvent, x: number, y: number):{ action: MouseStartAction, er?:EventRect } {
		const ctx = this.root().ctx;
		for (let er of this.eventShapes) {
			if (er.startDragShape && ctx.isPointInPath(er.startDragShape, x, y)) {
				return {action: er.event.movable ? "start" : "none", er: er};
			}
			if (er.endDragShape && ctx.isPointInPath(er.endDragShape, x, y)) {
				return {action: er.event.movable ? "end" : "none", er: er};
			}
			if (ctx.isPointInPath(er.shape, x, y)) {
				return { action: "event", er: er };
			}
		}
		return { action: "bg" };
	}


	onPointerDown(e: PointerEvent, x: number, y: number): boolean {
		this.startX = x;
		this.startY = y;
		//this.startTime = this.cursorToTime(x, y, false);

		const { action, er } = this.determineStartAction(e, x, y);
		this.action = action;
		this.actionObject = er;

		if (e.pointerType === "mouse") {
			switch (action) {
				case "start":
					this.startEventRange = { start: er.event.start, end: er.event.end };
					e.preventDefault();
					this.setPointerCapture(e);
					return DONE;
				case "end":
					this.startEventRange = { start: er.event.start, end: er.event.end };
					e.preventDefault();
					this.setPointerCapture(e);
					return DONE;
				case "event":
					if (er.event.movable) {
						this.root().canvas.setAttribute("draggable", "true");
						this.startEventRange = { start: er.event.start, end: er.event.end };
						return DONE;
					}
					break;
				case "bg":
					e.preventDefault();
					this.setPointerCapture(e);
					return DONE;
				default:
					this.action = "idle";
					this.actionObject = null;
			}
			return BUBBLE;
		} else {
			// Touch & Pen devices
			switch (action) {
				case "start":
				case "end":
				case "event":
					this.action = "event";	// ignore start/end
					if (er.event.movable) {
						this.root().canvas.setAttribute("draggable", "true");
						this.startEventRange = { start: er.event.start, end: er.event.end };
						return DONE;
					}
					break;
				case "bg":
					this.action = "event";
					this.root().canvas.setAttribute("draggable", "true");
					const start = this.cursorToTime(x, y, true);
					start.setTime(start.getTime() - 1800000);
					const end = new Date(start);
					end.setTime(end.getTime() + 3600000);
					this.actionObject = this.buildEventRect(this.dummyEvent(start, end));

					this.startEventRange = { start, end };
					return DONE;
				default:
					this.action = "idle";
					this.actionObject = null;
			}
			return BUBBLE;
		}
	}

	buildEventRect(e:CalEvent):EventRect {
		let col:DayColumn;
		for (let d of this.dayColumns.days) {
			if (sameDay(d.date, e.start)) {
				col = d;
				break;
			}
		}
		const tg = calcTimeGrid(this.view, this.height);
		const ypos = calcVertPosition(e.start, e.start, e.end, tg);
		const eventX = col.x+1;
		const eventWidth = col.width - 2;

		const shape = new Path2D();
		shape.roundRect(eventX, ypos.min, eventWidth, (ypos.max - ypos.min), 5);

		const er:EventRect = {
			x:eventX, y: ypos.min, width: eventWidth, height: (ypos.max - ypos.min),
			event:e,
			shape
		};
		return er;
	}

	dummyEvent(start:Date, end:Date):CalEvent {
		return { uid: DUMMY_ID,
			title: this.root().env.msg("event.create.dummy"),
			color: (this.view.defaultColor ?? DEFAULT_EVENT_COLOR),
			start, end };
	}


	onPointerMove(e: PointerEvent, x: number, y: number): boolean {
		if (e.pointerType === "mouse") {
			this.cursor = null;
			if (this.action === "idle") {
				const { action, er } = this.determineStartAction(e, x, y);
				if (action === "event") this.cursorInPreviewObject(er, uidToPreviewKey(er.event.uid));
				else this.cursorOutOfPreviewObject();
				switch (action) {
					case "start":
						this.cursor = "ns-resize";
						break;
					case "end":
						this.cursor = "ns-resize";
						break;
					case "event":
						this.cursor = "pointer";
						break;
					default:
						this.cursor = "default";
						break;
				}
			} else if (this.action === "bg" || this.action === "start" || this.action === "end") {
				// Waiting to move to start action and keeping the cursor
				const reallyStart:boolean = (Math.max(Math.abs(this.startX - x), Math.abs(this.startY - y))) > 2;
				if (reallyStart) {
					// Mouse moved enough now we know
					switch (this.action) {
						case "bg":
							this.action = "create";
							this.cursor = "default";
							const dummyStart = this.cursorToTime(this.startX, this.startY, true);
							let dummyEnd = this.cursorToTime(x, y, true);
							let snapMillis = (this.view.snapMins ?? 1) * 60000;
							if ((dummyEnd.getTime() - dummyStart.getTime()) < snapMillis) dummyEnd = new Date(dummyStart.getTime() + snapMillis);
							const root = this.root();
							this.createDummy = this.dummyEvent(dummyStart, dummyEnd);
							this.dirty(false);
							break;
						case "start":
							this.action = "resize-start";
							this.cursor = "ns-resize";
							this.view.store.manipulationStart(this.actionObject.event.uid);
							break;
						case "end":
							this.action = "resize-end";
							this.cursor = "ns-resize";
							this.view.store.manipulationStart(this.actionObject.event.uid);
							break;
					}
				}
			} else {
				let time;
				if (this.actionObject) {
					switch (this.action) {
						case "resize-start":
							time = this.cursorToTime(x, y, true);
							if (time) this.actionObject.event.start = time;
							this.cursor = "ns-resize";
							break;
						case "resize-end":
							time = this.cursorToTime(x, y, true);
							if (time) this.actionObject.event.end = time;
							this.cursor = "ns-resize";
							break;
					}
					this.dirty(false);
				} else {
					if (this.action === "create") {
						time = this.cursorToTime(x, y, true);
						if (time) {
							if (e.altKey) {
								const offset = time.getTime() - this.createDummy.end.getTime();
								this.createDummy.end = time;
								this.createDummy.start = new Date(this.createDummy.start.getTime() + offset);
								this.cursor = "grab";
							} else {
								this.createDummy.end = time;
								this.cursor = "ns-resize";
							}
							this.dirty(false);
						}
					}
				}
			}
			return true;
		} else {
			// Touch & Pen devices
			if (this.action === "idle") {
				// move without previous pointer-down ?
			} else if (this.action === "bg") {
				const reallyStart:boolean = (Math.max(Math.abs(this.startX - x), Math.abs(this.startY - y))) > 8;
				if (reallyStart) {
				}
				return DONE;
			} else if (this.action === "create") {
				e.preventDefault();
				e.stopPropagation();
				let time = this.cursorToTime(x, y, true);
				if (time) {
					this.createDummy.start = time;
					this.createDummy.end = new Date(this.createDummy.start.getTime() + 3600000);
					this.dirty(false);
				}
				return DONE;
			}
		}
	}


	onPointerUp(e: PointerEvent, x: number, y: number): boolean {
		if (e.pointerType === "mouse") {
			switch (this.action) {
				case "idle":	// nothing happens
					break;
				case "bg":	// candidates for future action
				case "start":
				case "end":
					this.view.store.manipulationEnd();
					break;
				case "create":
					this.view.store.invokeCreate(false, this.createDummy.start, this.createDummy.end);
					this.createDummy = null;
					break;
				case "resize-start":
					const newStart = this.actionObject.event.start;
					this.actionObject.event.start = this.startEventRange.start;
					this.view.store.update(this.actionObject.event.uid).setStart(newStart).commit();
					this.view.store.manipulationEnd();
					break;
				case "resize-end":
					const newEnd = this.actionObject.event.end;
					this.actionObject.event.end = this.startEventRange.end;
					this.view.store.update(this.actionObject.event.uid).setEnd(newEnd).commit();
					this.view.store.manipulationEnd();
					break;
				case "event":
					this.view.store.invokeEdit(this.actionObject.event.uid);
					break;
			}
			this.action = "idle";
			this.actionObject = null;
			return true;
		} else {
			// Touch & Pen devices
			switch (this.action) {
				case "create":
					this.view.store.invokeCreate(false, this.createDummy.start, this.createDummy.end);
					this.createDummy = null;
					break;
				case "event":
					this.view.store.invokeEdit(this.actionObject.event.uid);
					break;
			}
			this.action = "idle";
			this.actionObject = null;
			return true;
		}
	}
}

export interface TimeSeparator {
	type: "1h" | "30m" | "15m";
	y: number;	// y-position of the line relative to hour start
}

export interface TimeGrid {
	height: number;	// integer height of the whole hour
	separators: TimeSeparator[];
	cursor: number;	// current time y-position relative to day start
}



export function dayHours(view:CalendarView, height:number):number {
	// How many hours can view fit
	const minHours = Math.ceil(height / MAX_HOUR_HEIGHT);
	const maxHours = Math.floor(height / MIN_HOUR_HEIGHT);

	const dh = view.hours;
	if (typeof dh != "number") return 10;
	if (dh < minHours) return minHours;
	if (dh > maxHours) return maxHours;
	return dh;
}

/**
 * Calculates hour-lining based on component height and view properties
 * @param view
 * @param height of the component area
 */
export function calcTimeGrid(view: CalendarView, height:number):TimeGrid {
	const hours = dayHours(view, height);
	let hourHeight = height / hours;
	// Make hour height divisible by 4
	hourHeight = Math.round(hourHeight / 4) * 4;
	// Calc timer
	const now = new Date();
	const cursorPos = yPosition(now, hourHeight) + 0.5;

	const tg:TimeGrid = { height: hourHeight, separators: [], cursor:cursorPos };
	tg.separators.push({ type: "1h", y: 0.5 });
	hourHeight = hourHeight / 2;
	if (hourHeight < 16) return tg;
	tg.separators.push({ type: "30m", y: hourHeight + 0.5 });
	hourHeight = hourHeight / 2;
	if (hourHeight < 16) return tg;
	tg.separators.push({ type: "15m", y: hourHeight + 0.5 });
	tg.separators.push({ type: "15m", y: 3*hourHeight + 0.5 });
	return tg;
}

function yPosition(d:Date, hourHeight:number):number {
	return (d.getHours() + d.getMinutes() / 60) * hourHeight;
}

const MIN_EVENT_HEIGHT = 16;

/**
 * Calculate Y-position
 * @param day - day being drawn
 * @param start - start of the event
 * @param end - end of the event
 * @param tg - grid metrics
 **/
export function calcVertPosition(day:Date, start: Date, end: Date, tg: TimeGrid):Range {
	const dayStart = new Date(day);
	dayStart.setHours(0, 0, 0, 0);
	const dayEnd = new Date(dayStart);
	shiftDate(dayEnd);

	if (start.getTime() > end.getTime()) {
		const swap = start;
		start = end;
		end = swap;
	}
	let ystart: number;
	if (start.getTime() < dayStart.getTime()) ystart = 0;
	else ystart = yPosition(start, tg.height);

	let yend: number;
	if (end.getTime() >= dayEnd.getTime()) yend = 24 * tg.height;
	else yend = yPosition(end, tg.height);

	if ((yend-ystart) < MIN_EVENT_HEIGHT) yend = ystart + MIN_EVENT_HEIGHT;
	return { min: ystart, max: yend };
}


export function strokeCanceled(event: CalEvent, dc: DrawingContext, x: number, y: number, width: number, height: number) {
	if (event.canceled) {
		const redColor = dc.style.getPropertyValue("--red");
		dc.ctx.strokeStyle = redColor+"80";
		dc.ctx.lineWidth = 1;
		dc.ctx.beginPath();
		if (height < 25) {
			// Just one line stroke
			dc.ctx.moveTo(x + 5, y + height / 2);
			dc.ctx.lineTo(x + width - 5, y + height / 2);
		} else {
			dc.ctx.moveTo(x + 5, y + 5);
			dc.ctx.lineTo(x + width - 5, y + height - 5);
			dc.ctx.moveTo(x + width - 5, y + 5);
			dc.ctx.lineTo(x + 5, y + height - 5);
		}
		dc.ctx.stroke();
	}
}

export function priorityBorderColor(priority:number, fg: string):string {
	if (priority === 1) return fg + "88";
	else if (priority === 2) return fg + "55";
	else return fg + "22";
}

export function drawEventBlock(dc:DrawingContext, event:CalEvent, x:number, y:number, width:number, height:number, shadow=false, view?:CalendarView):EventRect {
	//return { x:0, y: 0, width:0, height:0, event, shape: new Path2D()};
	const ff = dc.style.getPropertyValue("--font");
	let fg = dc.style.getPropertyValue("--cal-fg");
	const selectionColor = dc.style.getPropertyValue("--selection-bg");
	try {
		dc.ctx.save();
		const dummy = DUMMY_ID === event.uid;
		let opacity:number = 1;
		if (event.state === "move") opacity = MOVE_OPACITY;
		if (event.canceled) opacity *= CANCELED_OPACITY;
		else if (event.completed) opacity *= DONE_OPACITY;
		if (opacity < 1) {
			dc.ctx.globalAlpha = opacity;
		}
		let c = event.color;
		if (typeof c !== "number" || c < 0 || c >= COLOR_NAMES.length) c = 3;	// default color
		const colorName = COLOR_NAMES[c];
		let boxColor = dc.style.getPropertyValue("--" + colorName + "-bg");
		if (event.completed || event.canceled) {
			let rgb = parseHexColor(boxColor);
			rgb = reduceSaturation(rgb.r, rgb.g, rgb.b, DONE_SATURATION);
			boxColor = rgbToHex(rgb);
		}
		dc.ctx.fillStyle = boxColor;
		const er:Partial<EventRect> = { event, x, y, width, height };
		er.shape = new Path2D();
		er.shape.roundRect(x, y, width, height, 5);
		if (shadow) {
			dc.ctx.shadowColor = "#00000022";
			dc.ctx.shadowBlur = 8;
			dc.ctx.shadowOffsetX = 1;
			dc.ctx.shadowOffsetY = 1;
		}
		dc.ctx.fill(er.shape);

		// Border
		dc.ctx.strokeStyle = priorityBorderColor(event.priority, fg);
		dc.ctx.beginPath();
		dc.ctx.roundRect(x + 0.5, y + 0.5, width - 1, height - 1, 4.5);
		dc.ctx.stroke(er.shape);


		if (shadow) {
			dc.ctx.shadowColor = 'transparent';
			dc.ctx.shadowBlur = 0;
		}
		if (dummy) {
			// Create 2px border around dummy event
			dc.ctx.strokeStyle = fg = selectionColor;
			dc.ctx.lineWidth = 2;
			dc.ctx.beginPath();
			dc.ctx.roundRect(x + 1, y + 1, width - 2, height - 2, 4);
			dc.ctx.stroke();
		}
		dc.ctx.clip(er.shape);

		if (event.movable) {
			er.startDragShape = new Path2D();
			er.startDragShape.rect(x, y, width, 4);
			er.endDragShape = new Path2D();
			er.endDragShape.rect(x, y + height - 4, width, 4);
		}

		const PADX = 4;
		const PADY = 4;
		const tx = x + PADX;
		const ty = y + PADY;
		const twidth = width - 2 * PADX;
		const theight = height - 2 * PADY;

		const pi = Icon.parse("call");
		//const eventIcon = decodeNumericIconEntity(pi.entity);
		let fontSize = 12;
		if (width < 120) fontSize = width / 10;
		if (fontSize < 8) fontSize = 8;
		const lh = Math.floor(fontSize * 1.5);
		fontSize = Math.floor(fontSize);
		const iconSize = Math.round(14 * fontSize / 12);

		let showTime = true;
		let display = DISPLAY_SUBJECT+DISPLAY_PLACE;
		let showAttendees: "no" | "mono" | "full" = "no";
		if (view) {
			showTime = view.showTime;
			display = view.display;
			showAttendees = view.showAttendees;
		}

		const runs:TextRun[] = [];
		const separ:TextRun = { text: " ~ ", font: fontSize+"px "+ff, color: fg, opacity: 0.5 };
		if (showTime || dummy) runs.push({ text: timeRange(event)+" ", font: fontSize+"px "+ff, color: fg, opacity: 0.5 });

		let first = true;
		for (let i = 0; i < display.length; i++) {
			switch (display.charAt(i)) {
				case DISPLAY_SUBJECT:
					if (first) first = false;
					else runs.push(separ);
					let title = event.title;
					if (event.icon) {
						const eicon = decodeNumericIconEntity(Icon.parse(event.icon).entity);
						runs.push({ text: eicon, font: iconSize+"px "+ICON_FONT, color: fg });
						title = " "+title;	// add space to separate from the icon
					}
					runs.push({ text: title, font: "500 "+fontSize+"px "+ff, color: fg });
					break;
				case DISPLAY_PLACE:
					if (event.place) {
						if (first) first = false;
						else runs.push(separ);
						runs.push({ text: event.place, font: fontSize+"px "+ff, color: fg, opacity: 0.3 });
					}
					break;
				case DISPLAY_CUSTOMER:
					if (event.partyName) {
						if (first) first = false;
						else runs.push(separ);
						runs.push({ text: event.partyName, font: fontSize+"px "+ff, color: fg, opacity: 0.75 });
					}
					break;
				case DISPLAY_DESCRIPTION:
					if (event.comment) {
						if (first) first = false;
						else runs.push(separ);
						runs.push({ text: event.comment, font: fontSize+"px "+ff, color: fg, opacity: 0.5 });
					}
					break;
			}
		}
		if (showAttendees !== "no" && Array.isArray(event.attendees) && event.attendees.length > 0) {
			if (!first) runs.push(separ);
			const names:string[] = [];
			event.attendees.forEach((a) => {
				names.push(a.name);
			});
			runs.push({ text: names.join(", "), font: fontSize+"px "+ff, color: fg, opacity: 0.3 });
		}

		drawBlockText(dc.ctx, runs, tx, ty, twidth, theight, lh);

		/** Cross strike canceled event */
		strokeCanceled(event, dc, x, y, width, height);

		return er as EventRect;

	} finally {
		dc.ctx.restore();
	}
}


function createEventDragImage(er: EventRect, root: CanvasRootComponent) {
	const canvas:HTMLCanvasElement = document.createElement('canvas');
	canvas.style.width = er.width + "px";
	canvas.style.height = er.height + "px";
	canvas.style.position = 'absolute';
	canvas.style.top = "-9000px";
	const ctx = canvas.getContext('2d');
	// Set canvas size
	canvas.width = root.ratio *  er.width;
	canvas.height = root.ratio *  er.height;
	ctx.scale(root.ratio, root.ratio);

	drawEventBlock({ ctx: ctx, style: root.style, env: root.env, props:{} }, er.event, 0, 0, er.width, er.height, false);

	document.body.appendChild(canvas);
	setTimeout(() => canvas.remove(), 0);
	return canvas;
}

export function setNewDateAfterScrolling(view: CalendarView, dayColumns: DayColumns):void {
	const cols = dayColumns.days.length;
	if (cols > 0) {
		let selected:Date = dayColumns.days[0].date
		saveView(view, selected);
	}
}

export const PLUS_RADIUS = 8.5;

export function drawPlusBullet(dc: DrawingContext, cx:number, cy:number):void {

	try {
		dc.ctx.save();

		dc.ctx.strokeStyle = dc.style.getPropertyValue("--cal-bg");
		dc.ctx.fillStyle = dc.style.getPropertyValue("--green");

		dc.ctx.lineWidth = 1;
		dc.ctx.beginPath();
		dc.ctx.ellipse(cx, cy, PLUS_RADIUS, PLUS_RADIUS, 0, 0, Math.PI * 2);
		dc.ctx.fill();
		dc.ctx.stroke();

		dc.ctx.lineWidth = 2;
		dc.ctx.beginPath();
		dc.ctx.moveTo(cx, cy - PLUS_RADIUS + 4);
		dc.ctx.lineTo(cx, cy + PLUS_RADIUS - 4);
		dc.ctx.stroke();
		dc.ctx.beginPath();
		dc.ctx.moveTo(cx - PLUS_RADIUS + 4, cy);
		dc.ctx.lineTo(cx + PLUS_RADIUS - 4, cy);
		dc.ctx.stroke();
	} finally {
		dc.ctx.restore();
	}
}